import {
  createOpenMediaManagerFn,
  MediaManagerMediaType
} from "../create-open-media-manager-fn.js";
import { getHighestQualityVideo } from "../get-highest-quality-video.js";
import { getUploadError } from "../get-upload-error.js";
import { getMediaFileType } from "../getMediaFileType.js";
import { logInvariant } from "../log-invariant.js";
import { createUploadFn } from "../request-service-provider/media-manager-uploader.js";
import { unreachable } from "../uncreachable.js";
import { upload } from "../upload/upload.js";
import { withFileSizeLimitation } from "../with-file-size-limitation.js";
const ACCEPT_MIME_TYPE = "*";
const handleFileUpload = async ({
  services
}) => {
  const onFileSelected = createOnFileSelectedFn(services);
  const pickMedia = getFilePickMedia({ services, onFileSelected });
  const result = await pickMedia({
    mimeType: ACCEPT_MIME_TYPE,
    multiple: false
  });
  switch (result.type) {
    case "ready-media": {
      return result.media[0];
    }
    case "file": {
      const files = result.file;
      files.map(async (_file) => {
        const file = new File([_file], _file.name, { type: _file.type });
        try {
          const uploadFn = result.customUploadFn || onFileSelected;
          if (!uploadFn) {
            logInvariant(Error("No upload function provided"));
            throw new Error("No upload function provided");
          }
          const uploadedFile = await upload(uploadFn, file);
          const uploadData = uploadedFile.data;
          return uploadData.url;
        } catch (error) {
          services.uploadService.notifyError(error);
          return void 0;
        }
      });
      return void 0;
    }
    case "cancel": {
      return;
    }
    default: {
      console.error(unreachable(result));
    }
  }
};
const getFilePickMedia = function({
  services,
  onFileSelected
}) {
  const devPickMedia = services.devService().pickMedia;
  const editorPickMedia = services.editorProps.pickMedia?.pickFile;
  const handleFileSelection = services.devService().handleFileSelection ?? createHandleFileSelectionFn(services);
  return devPickMedia ? devPickMedia : editorPickMedia ? editorPickMedia : handleFileSelection ? async () => {
    const data = await new Promise(
      (resolve) => handleFileSelection(({ data: data2 }) => resolve(data2))
    );
    return { type: "ready-media", media: data instanceof Array ? data : [data] };
  } : async ({ mimeType }) => {
    const files = await services.uploadService?.selectFiles({ accept: mimeType, multiple: false }) ?? [];
    return { type: "file", file: files, customUploadFn: onFileSelected };
  };
};
const createOnFileSelectedFn = (services) => {
  const { metaSiteId, uid, siteOwnerId } = services.wixInstance();
  if (!metaSiteId) {
    return void 0;
  }
  const fileUpload = (file, updateEntity) => {
    const upload2 = createUploadFn(services);
    const fileType = getMediaFileType(file.name);
    const privacy = "private";
    return upload2(file, fileType, { privacy }).then(({ original_file_name: name, media_type, file_url, file_output }) => {
      const fileUrl = file_output?.video ? getHighestQualityVideo(file_output.video).url : file_url;
      const type = file.name.split(".").length > 1 ? file.name.split(".").pop() : media_type;
      const componentData = getFilePluginComponentData(name, fileUrl, type || "", privacy);
      updateEntity({ data: componentData });
    }).catch((error) => {
      const isOwner = siteOwnerId === uid;
      const mediaUploadError = getUploadError(error, metaSiteId, isOwner);
      updateEntity({ error: mediaUploadError });
    });
  };
  return withFileSizeLimitation(fileUpload, services.editorProps.fileUploadSizeLimit);
};
const createHandleFileSelectionFn = (services) => {
  const openMediaManager = createOpenMediaManagerFn();
  if (!openMediaManager) {
    return void 0;
  }
  return (updateEntity) => openMediaManager({
    mediaType: MediaManagerMediaType.ALL_MEDIA,
    appDefId: services.wixInstance().appDefId
  }).then((items) => {
    const componentData = getFilePluginComponentDataFromMediaManager(items[0]);
    updateEntity({ data: [componentData] });
  });
};
const getFilePluginComponentDataFromMediaManager = (file) => {
  const { title: name, fileUrl, fileName, fileSize: size, fileOutput } = file;
  let url = fileUrl;
  let type = (fileName.split(".").length === 1 ? name : fileName).split(".").pop();
  if (fileOutput?.video) {
    const video = getHighestQualityVideo(fileOutput.video);
    url = video.url;
    type = video.format;
  }
  if (!type) {
    type = "unknown";
    console.error("could not determine file type for file:", file);
  }
  return getFilePluginComponentData(name, url, type, "public", size);
};
const getFilePluginComponentData = (name, fileUrl, type, privacy = "public", size) => {
  const componentData = {
    name,
    type,
    id: fileUrl,
    privacy
  };
  return size ? { ...componentData, size } : componentData;
};
export {
  handleFileUpload
};
//# sourceMappingURL=handle-file-upload.js.map
